import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService, TranslateLoader } from '@ngx-translate/core';
import { StorageService } from '../storage/storage.service';
import { Platform } from '@ionic/angular';
import { Broadcaster } from '../events/broadcaster.class';
import { ILanguageItem } from './models/language.model';
import { delay, finalize, first, map, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { PlatformService } from '../platform/platform.service';
import { ShareExtensionService } from '../share-extension/share-extension.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private storageKeyUserSelectedLanguage: string = 'userLanguage';
  private httpErrorResponse: HttpErrorResponse;
  private logger: Logger;
  public isCordova: boolean = this.platformService.isCordova();

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private loggingService: LoggingService,
    private platform: Platform,
    private platformService: PlatformService,
    private httpClient: HttpClient,
    private broadCaster: Broadcaster,
    private shareExtensionService: ShareExtensionService
  ) {
    this.logger = this.loggingService.getLogger("[LanguageService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.setListeners();
   }

   private setListeners(): void {
    this.platform.ready().then(() => {
      if (this.isCordova) {
        this.storageService.getDatabaseState().subscribe((isReady: boolean) => {
          if (isReady) {
            this.setTranslationData();
          }
        });
      } else {
        this.setTranslationData();
      }
    });
  }

  private setTranslationData(): void {
    this.logger.info('setTranslationData()');
    this.storageService.getItemFromLocalStorage(this.storageKeyUserSelectedLanguage, 'setTranslationData()')
    .pipe(
      first() // Automatically unsubscribe after the first value is received
    ).subscribe({
      next: (userSelectedLanguage: string | null) => {
        if (userSelectedLanguage) {
          this.translate.use(userSelectedLanguage);
          this.shareExtensionService.setSelectedLanguage(userSelectedLanguage);
        } else {
          const browserLanguage = this.translate.getBrowserLang() || 'en';
          const languageKeyToUse: string = this.extractLanguageKey(browserLanguage);
          this.translate.use(languageKeyToUse);
        }
      },
      error: (e: unknown) => {
        this.logger.error('setTranslationData() ERROR occurred while retrieving language preference ' + JSON.stringify({ error: e instanceof Error ? e.message : String(e) }));
      }
    });
  }

  private extractLanguageKey(languageKey: string): string {
    this.logger.info('extractLanguageKey()');
    let languageKeyToUse: string = '';
    if (languageKey.split('_').length > 1) {
      languageKeyToUse = languageKey.split('_')[0];
    } else if (languageKey.split('-').length > 1) {
      languageKeyToUse = languageKey.split('-')[0];
      if (languageKeyToUse === 'zh') { // For chinese languages
        const chineseCode = (languageKey.split('-')[1]).toLowerCase();
        languageKeyToUse = languageKeyToUse + '-' + chineseCode;
      }
    } else {
      languageKeyToUse = languageKey;
    }
    return languageKeyToUse;
  }

  public getLanguages(): Observable<Array<ILanguageItem>> {
    this.logger.info('getLanguages()');
    const endpointLanguagesSupported = 'https://assets.printix.net/captions/Captions.2025.4.json';
    return new Observable((observer) => {
      this.httpClient.get<any>(endpointLanguagesSupported)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        // return httpErrorResponse.status > 499 ? Observable.of(true) : Observable.throw(httpErrorResponse);
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((languageItems: any) => {
        const languageItemsList: Array<ILanguageItem> = [];
        for (const languageItem of languageItems.Languages) {
          languageItemsList.push({
            codeLanguage: languageItem.LanguageCode,
            codeFlag: languageItem.LanguageCode,
            nameEnglish: languageItem.EnglishName,
            nameNative: languageItem.NativeName
          });
        }
        observer.next(languageItemsList);
        observer.complete();
      });
    });
  }

  public getCurrentLanguageCode(): string {
    return this.translate.currentLang;
  }

  public changeLanguage(languageKey: string): void {
    this.logger.info('changeLanguage() called');
    if (languageKey) {
      const languageKeyToUse: string = this.extractLanguageKey(languageKey);
      this.translate.use(languageKeyToUse);
      this.storageService.addItemToLocalStorage(this.storageKeyUserSelectedLanguage, languageKeyToUse, 'changeLanguage()')
      .subscribe(() => {
        this.translate.setDefaultLang(languageKeyToUse);
        this.broadCaster.broadcast('languageChange', languageKey); // Broadcast to change language dynamically for moment.js relative time library
        this.shareExtensionService.setSelectedLanguage(languageKeyToUse);
      });
    }
  }

}

export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient, private prefix: string, private suffix: string) {}

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`).pipe(
      map((translations: any) => {
        // The remote bundle has no caption for the IN_PROGRESS_SENT job state, so it
        // would otherwise render the raw key. Alias it to the existing, already-localized
        // PRINTING caption so the job list reads "Printing". Only fill it in when absent,
        // so this stopgap self-removes once the bundle ships a real IN_PROGRESS_SENT caption.
        if (translations && !translations['IN_PROGRESS_SENT']) {
          translations.IN_PROGRESS_SENT = translations['PRINTING'] ?? 'Printing';
        }
        return translations;
      })
    );
  }
}

export function TranslateHttpLoaderFactory(http: HttpClient) {
  const endpointLanguages = 'https://assets.printix.net/captions/Caption_App_';
  return new CustomTranslateLoader(http, endpointLanguages, '.json');
}
